import { ref } from 'vue';
import axios from 'axios';
class ShareService {
    constructor() {
        this.shareCode = ref(null);
        this.isSubmitting = ref(false);
        this.error = ref(null);
    }
    getVehicleName(vehicleDetails) {
        if (!vehicleDetails)
            return '';
        const { make_title, model_title, engine_build, power_kw, year_start, year_end } = vehicleDetails;
        const yearRange = year_end
            ? `${year_start} - ${year_end}`
            : `${year_start}+`;
        return `${make_title} ${model_title} ${engine_build} (${power_kw} kW) (${yearRange})`;
    }
    async generateShareCode(vehicleDetails) {
        // Return cached share code if available
        if (this.shareCode.value) {
            return this.shareCode.value;
        }
        if (!vehicleDetails) {
            throw new Error('Vehicle details are required');
        }
        try {
            const response = await axios.post('/api/oil-finder/share', {
                vehicleName: this.getVehicleName(vehicleDetails) + ' ',
                vehicleTypeId: vehicleDetails.type_id ?? null
            });
            this.shareCode.value = response.data.data.shareCode;
            return this.shareCode.value;
        }
        catch (err) {
            throw new Error(err.response?.data?.message || 'Failed to generate share code');
        }
    }
    async shareViaEmail(formData, vehicleDetails) {
        if (!formData.email) {
            throw new Error('Email is required');
        }
        this.isSubmitting.value = true;
        this.error.value = null;
        try {
            await axios.post('/api/oil-finder/share', {
                email: formData.email,
                vehicleName: this.getVehicleName(vehicleDetails) + ' ',
                vehicleTypeId: vehicleDetails?.type_id ?? null
            });
        }
        catch (err) {
            const errorMessage = err.response?.data?.message ||
                'Something went wrong. Please try again.';
            this.error.value = errorMessage;
            throw new Error(errorMessage);
        }
        finally {
            this.isSubmitting.value = false;
        }
    }
    async shareViaDevice(vehicleDetails) {
        if (!navigator.share) {
            throw new Error('Web Share API is not supported');
        }
        const vehicleName = this.getVehicleName(vehicleDetails);
        const shareCode = await this.generateShareCode(vehicleDetails);
        const shareUrl = `${window.location.origin}/find-my-oil?shareCode=${shareCode}`;
        try {
            await navigator.share({
                title: 'Duckhams Oil Product Recommendations',
                text: `Check out these recommendations for ${vehicleName}`,
                url: shareUrl
            });
        }
        catch (err) {
            if (err.name !== 'AbortError') {
                throw err;
            }
        }
    }
    clearShareCode() {
        this.shareCode.value = null;
    }
    resetError() {
        this.error.value = null;
    }
    get isLoading() {
        return this.isSubmitting.value;
    }
    get errorMessage() {
        return this.error.value;
    }
    get cachedShareCode() {
        return this.shareCode.value;
    }
}
// Export singleton instance
let shareServiceInstance = null;
export function useShareService() {
    if (!shareServiceInstance) {
        shareServiceInstance = new ShareService();
    }
    return shareServiceInstance;
}
